import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'

// Small controls in the dashboard's style (Stromkreise inspector and toolbar).

export function Icon({ name, size = 18, fill = false, style, className = '' }) {
  return <span className={`material-symbols-outlined ${fill ? 'icon-fill' : ''} ${className}`} style={{ fontSize: size, ...style }} aria-hidden="true">{name}</span>
}

export function Logo({ size = 28, word = false }) {
  return (
    <span className="inline-flex items-center gap-2">
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <rect width="64" height="64" rx="15" fill="#1b1c1b" stroke="rgba(255,255,255,0.08)" />
        <path d="M32 9 46 31H38L48 46H16L26 31H18Z" fill="#b0ceae" />
        <rect x="29" y="46" width="6" height="9" rx="1.5" fill="#c69a6a" />
      </svg>
      {word && <span className="font-headline font-extrabold" style={{ fontSize: size * 0.62, letterSpacing: '-0.03em' }}>Mori</span>}
    </span>
  )
}

export function IconBtn({ icon, label, onClick, disabled, active, accent, size = 18, kbd }) {
  return (
    <button type="button" className="btn btn-ghost btn-icon" onClick={onClick} disabled={disabled}
      title={kbd ? `${label} (${kbd})` : label} aria-label={label} aria-pressed={active ? 'true' : undefined}
      style={{ color: active ? 'var(--sage)' : accent ?? undefined, background: active ? 'rgba(176,206,174,0.1)' : undefined }}>
      <Icon name={icon} size={size} />
    </button>
  )
}

export function Group({ children, className = '' }) {
  return <div className={`flex items-center gap-0.5 inner-panel bg-surface-container-low px-1 py-0.5 ${className}`} style={{ border: '1px solid var(--line)' }}>{children}</div>
}

export function Label({ children, right, className = '' }) {
  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>
      <span className="ds-label">{children}</span>
      {right}
    </div>
  )
}

/** Number input that commits on Enter/blur; empty = auto (placeholder shows the auto value). */
export function NumField({ value, onChange, unit = 'mm', step = 1, min, max, placeholder, title, autoValue, width }) {
  const [text, setText] = useState(value ?? '')
  const [focus, setFocus] = useState(false)
  const shown = focus ? text : value === null || value === undefined || value === '' ? '' : fmt(value)
  const commit = () => {
    const t = String(text).trim().replace(',', '.')
    if (t === '') { if (autoValue !== undefined) onChange(null); return }
    let v = Number(t)
    if (!Number.isFinite(v)) return
    if (min !== undefined) v = Math.max(min, v)
    if (max !== undefined) v = Math.min(max, v)
    if (v !== value) onChange(v)
  }
  return (
    <label className={`num-field ${autoValue !== undefined && (value === null || value === undefined) ? 'auto' : ''}`} title={title} style={{ width }}>
      <input className="ds-input" inputMode="decimal" value={shown} placeholder={placeholder ?? (autoValue !== undefined ? `${fmt(autoValue)} auto` : '')}
        onFocus={() => { setFocus(true); setText(value ?? '') }}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => { setFocus(false); commit() }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') { setText(value ?? ''); e.currentTarget.blur() }
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault()
            const base = Number(String(text).replace(',', '.')) || Number(value) || 0
            const nv = Math.round((base + (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1)) * 100) / 100
            setText(String(nv)); onChange(nv)
          }
        }} />
      {unit && <span className="unit">{unit}</span>}
    </label>
  )
}
const fmt = (v) => (typeof v === 'number' ? String(Math.round(v * 100) / 100).replace('.', ',') : v)

export function Field({ label, children, hint }) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="text-outline" style={{ fontSize: 11 }}>{label}</span>
      {children}
      {hint && <span className="text-outline-faint" style={{ fontSize: 10.5 }}>{hint}</span>}
    </label>
  )
}

export function Select({ value, onChange, options, title }) {
  return (
    <select className="ds-input" value={value} onChange={(e) => onChange(e.target.value)} title={title} style={{ fontSize: 12.5 }}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

export function Seg({ value, onChange, options, small }) {
  return (
    <div className="seg" role="group">
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)} title={o.title || o.label}
          style={small ? { padding: '3px 8px', fontSize: 11 } : undefined}>
          {o.icon && <Icon name={o.icon} size={15} />}{o.label}
        </button>
      ))}
    </div>
  )
}

export function Toggle({ checked, onChange, label }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className="flex items-center gap-2" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
      <span style={{ width: 30, height: 18, borderRadius: 99, background: checked ? 'var(--sage)' : 'rgba(255,255,255,0.12)', position: 'relative', transition: 'background 0.15s' }}>
        <span style={{ position: 'absolute', top: 2, left: checked ? 14 : 2, width: 14, height: 14, borderRadius: 99, background: checked ? '#1d361f' : '#c3c8bf', transition: 'left 0.15s' }} />
      </span>
      {label && <span className="text-on-surface-variant" style={{ fontSize: 12.5 }}>{label}</span>}
    </button>
  )
}

export function Stat({ label, value, color, onClick, title }) {
  return (
    <button type="button" onClick={onClick} disabled={!onClick} title={title} className="flex flex-col items-start gap-0.5 px-3 py-2 text-left min-w-0 w-full"
      style={{ borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', cursor: onClick ? 'pointer' : 'default' }}>
      <span className="font-mono truncate max-w-full" style={{ fontSize: 15, fontWeight: 600, color: color ?? '#e4e2e0' }}>{value}</span>
      <span className="text-outline" style={{ fontSize: 10.5 }}>{label}</span>
    </button>
  )
}

/** 1–5 rating as five short bars. */
export function Bars({ value, color = 'var(--sage)', label }) {
  return (
    <span className="inline-flex items-center gap-1.5" title={label ? `${label}: ${value} von 5` : undefined}>
      {label && <span className="text-outline" style={{ fontSize: 10.5, minWidth: 58 }}>{label}</span>}
      <span className="inline-flex gap-[2px]">
        {[1, 2, 3, 4, 5].map((i) => <span key={i} style={{ width: 9, height: 4, borderRadius: 2, background: i <= value ? color : 'rgba(255,255,255,0.1)' }} />)}
      </span>
    </span>
  )
}

export function Modal({ open, onClose, title, subtitle, children, width = 720, footer }) {
  const [mounted, setMounted] = useState(open)
  if (open && !mounted) setMounted(true)
  useEffect(() => {
    if (open) return
    const t = setTimeout(() => setMounted(false), 200)
    return () => clearTimeout(t)
  }, [open])
  useEffect(() => {
    if (!open) return
    const k = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [open, onClose])
  if (!mounted) return null
  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)', opacity: open ? 1 : 0, transition: 'opacity 0.18s' }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-surface-container flex flex-col max-h-full" role="dialog" aria-modal="true" aria-label={title}
        style={{ width: `min(${width}px, 100%)`, borderRadius: 14, border: '1px solid var(--line-strong)', boxShadow: '0 30px 80px rgba(0,0,0,0.6)', transform: open ? 'none' : 'translateY(8px) scale(0.99)', transition: 'transform 0.18s' }}>
        <div className="flex items-center gap-3 px-5 pt-4 pb-3" style={{ borderBottom: '1px solid var(--line)' }}>
          <div className="flex-1 min-w-0">
            <p className="font-headline font-bold text-on-surface" style={{ fontSize: 16 }}>{title}</p>
            {subtitle && <p className="text-outline" style={{ fontSize: 12 }}>{subtitle}</p>}
          </div>
          <IconBtn icon="close" label="Schließen" onClick={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin p-5">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3" style={{ borderTop: '1px solid var(--line)' }}>{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

// Toasts: one at a time, bottom centre (like the wiring page).
let toastState = null
const toastSubs = new Set()
let toastTimer = 0
export function toast(text, kind = 'ok') {
  toastState = { text, kind, n: Date.now() }
  toastSubs.forEach((f) => f())
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => { toastState = null; toastSubs.forEach((f) => f()) }, kind === 'error' ? 6000 : 3200)
}
export function Toasts() {
  const t = useSyncExternalStore((f) => { toastSubs.add(f); return () => toastSubs.delete(f) }, () => toastState)
  return createPortal(
    t ? (
      <div key={t.n} role="status" className="fixed left-1/2 bottom-8 z-[2100] inner-panel px-4 py-2.5 flex items-center gap-2 mori-in"
        style={{ transform: 'translateX(-50%)', background: 'rgba(31,32,31,0.97)', border: `1px solid ${t.kind === 'error' ? 'rgba(255,180,171,0.35)' : 'rgba(176,206,174,0.3)'}`, maxWidth: 'min(560px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.45)' }}>
        <Icon name={t.kind === 'error' ? 'error' : 'check_circle'} size={17} style={{ color: t.kind === 'error' ? '#ffb4ab' : 'var(--sage)' }} />
        <span className="text-on-surface-variant" style={{ fontSize: 12.5 }}>{t.text}</span>
      </div>
    ) : null,
    document.body,
  )
}

export function useClickOutside(onOut) {
  const ref = useRef(null)
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onOut() }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [onOut])
  return ref
}
